import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, CheckCircle2, XCircle, ExternalLink, Phone, Mail,
  Building2, RefreshCw, Copy, Link2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const ChannelPartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/channel-partners`);
      setPartners(res.data.partners);
    } catch {
      toast.error("Failed to load channel partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  const handleApprove = async (partnerId) => {
    try {
      await axios.put(`${API}/channel-partners/${partnerId}/approve`);
      toast.success("Partner approved");
      fetchPartners();
    } catch {
      toast.error("Failed to approve partner");
    }
  };

  const handleDeactivate = async (partnerId) => {
    if (!window.confirm("Deactivate this partner? They will no longer be able to submit leads.")) return;
    try {
      await axios.put(`${API}/channel-partners/${partnerId}/deactivate`);
      toast.success("Partner deactivated");
      fetchPartners();
    } catch {
      toast.error("Failed to deactivate partner");
    }
  };

  const copyPortalLink = (code) => {
    const url = `${window.location.origin}/cp/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Portal link copied");
  };

  const active = partners.filter(p => p.active);
  const inactive = partners.filter(p => !p.active);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-64" />
            <div className="h-64 bg-slate-200 rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="channel-partners-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Channel Partners</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage partner access and track their lead submissions
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchPartners} data-testid="refresh-partners">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-slate-500" />
              <p className="text-2xl font-bold text-slate-900" data-testid="cp-total">{partners.length}</p>
              <p className="text-xs text-slate-500">Total Partners</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
              <p className="text-2xl font-bold text-emerald-600" data-testid="cp-active">{active.length}</p>
              <p className="text-xs text-emerald-600">Active</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="p-4 text-center">
              <XCircle className="w-5 h-5 mx-auto mb-1 text-amber-500" />
              <p className="text-2xl font-bold text-amber-600" data-testid="cp-inactive">{inactive.length}</p>
              <p className="text-xs text-amber-600">Pending / Inactive</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ExternalLink className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <p className="text-2xl font-bold text-blue-600">{partners.reduce((s, p) => s + (p.leads_submitted || 0), 0)}</p>
              <p className="text-xs text-slate-500">Leads Submitted</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approval */}
        {inactive.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                <XCircle className="w-4 h-4" /> Pending Approval / Inactive ({inactive.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50/50">
                    <TableHead>Partner</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead className="w-36">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inactive.map(p => (
                    <TableRow key={p.partner_id} data-testid={`cp-row-${p.partner_id}`}>
                      <TableCell>
                        <p className="font-medium text-slate-900">{p.name || "—"}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Building2 className="w-3.5 h-3.5" />
                          {p.company || "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5 text-xs">
                          {p.phone && <div className="flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" />{p.phone}</div>}
                          {p.email && <div className="flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" />{p.email}</div>}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{p.code}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{p.leads_submitted || 0}</TableCell>
                      <TableCell>
                        <Button size="sm" onClick={() => handleApprove(p.partner_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`approve-${p.partner_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Active Partners */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Active Partners ({active.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {active.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No active partners yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Partner</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead>Portal Link</TableHead>
                    <TableHead className="w-36">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.map(p => (
                    <TableRow key={p.partner_id} data-testid={`cp-row-${p.partner_id}`}>
                      <TableCell>
                        <p className="font-medium text-slate-900">{p.name || "—"}</p>
                        {p.approved_at && <p className="text-[10px] text-slate-400">Approved {new Date(p.approved_at).toLocaleDateString("en-IN")}</p>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Building2 className="w-3.5 h-3.5" />
                          {p.company || "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5 text-xs">
                          {p.phone && <div className="flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" />{p.phone}</div>}
                          {p.email && <div className="flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" />{p.email}</div>}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{p.code}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{p.leads_submitted || 0}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copyPortalLink(p.code)} data-testid={`copy-link-${p.partner_id}`}>
                          <Copy className="w-3 h-3 mr-1" /> Copy
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeactivate(p.partner_id)} data-testid={`deactivate-${p.partner_id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Deactivate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ChannelPartnersPage;
